
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {LegalDocumentMetadata} from './LegalDocumentMetadata';
import {FieldModelLegalDocumentMetadata} from './LegalDocumentMetadata';

/**
 * LegalDocumentDescription struct
 */
class LegalDocumentDescription {
  /**
   * Initialize struct
   * @param {!number=} documentId
   * @param {!number=} groupId
   * @param {!LegalDocumentMetadata=} metadata
   * @constructor
   */
  constructor (documentId = new UInt64(0, 0), groupId = new UInt64(0, 0), metadata = new LegalDocumentMetadata()) {
    this.DocumentId = documentId
    this.GroupId = groupId
    this.Metadata = metadata
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LegalDocumentDescription}
   * @param {!LegalDocumentDescription} other Other struct
   * @returns {!LegalDocumentDescription} This struct
   */
  copy (other) {
    if (other.DocumentId != null) {
      this.DocumentId = UInt64.fromNumber(other.DocumentId)
    } else {
      this.DocumentId = null
    }
    if (other.GroupId != null) {
      this.GroupId = UInt64.fromNumber(other.GroupId)
    } else {
      this.GroupId = null
    }
    if (other.Metadata != null) {
      this.Metadata = LegalDocumentMetadata.fromObject(other.Metadata)
    } else {
      this.Metadata = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LegalDocumentDescription}
   * @returns {!LegalDocumentDescription} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LegalDocumentDescriptionModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LegalDocumentDescriptionModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LegalDocumentDescription}
   * @param {!LegalDocumentDescription} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LegalDocumentDescription)) {
      throw new TypeError('Instance of LegalDocumentDescription is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LegalDocumentDescription}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      DocumentId: ((this.DocumentId != null) ? this.DocumentId.toNumber() : null),
      GroupId: ((this.GroupId != null) ? this.GroupId.toNumber() : null),
      Metadata: ((this.Metadata != null) ? this.Metadata : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LegalDocumentDescription.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LegalDocumentDescription} other Object value
   * @returns {!LegalDocumentDescription} Created struct
   */
  static fromObject (other) {
    return new LegalDocumentDescription().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LegalDocumentDescription}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LegalDocumentDescription.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LegalDocumentDescription}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6594
  }
}

export { LegalDocumentDescription };

/**
 * Fast Binary Encoding LegalDocumentDescription field model
 */
class FieldModelLegalDocumentDescription extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.DocumentId = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.GroupId = new fbe.FieldModelUInt64(buffer, this.DocumentId.fbeOffset + this.DocumentId.fbeSize)
    this.Metadata = new FieldModelLegalDocumentMetadata(buffer, this.GroupId.fbeOffset + this.GroupId.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelLegalDocumentDescription}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLegalDocumentDescription}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.DocumentId.fbeSize + this.GroupId.fbeSize + this.Metadata.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLegalDocumentDescription}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.DocumentId.fbeExtra + this.GroupId.fbeExtra + this.Metadata.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLegalDocumentDescription}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLegalDocumentDescription.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLegalDocumentDescription}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6594
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLegalDocumentDescription}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLegalDocumentDescription}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DocumentId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DocumentId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DocumentId.fbeSize

    if ((fbeCurrentSize + this.GroupId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.GroupId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.GroupId.fbeSize

    if ((fbeCurrentSize + this.Metadata.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Metadata.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Metadata.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLegalDocumentDescription}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLegalDocumentDescription}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLegalDocumentDescription}
   * @param {!LegalDocumentDescription} fbeValue Default value, defaults is new LegalDocumentDescription()
   * @returns {!LegalDocumentDescription} LegalDocumentDescription value
   */
  get (fbeValue = new LegalDocumentDescription()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLegalDocumentDescription}
   * @param {!LegalDocumentDescription} fbeValue LegalDocumentDescription value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DocumentId.fbeSize) <= fbeStructSize) {
      fbeValue.DocumentId = this.DocumentId.get()
    } else {
      fbeValue.DocumentId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DocumentId.fbeSize

    if ((fbeCurrentSize + this.GroupId.fbeSize) <= fbeStructSize) {
      fbeValue.GroupId = this.GroupId.get()
    } else {
      fbeValue.GroupId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.GroupId.fbeSize

    if ((fbeCurrentSize + this.Metadata.fbeSize) <= fbeStructSize) {
      fbeValue.Metadata = this.Metadata.get()
    } else {
      fbeValue.Metadata = new LegalDocumentMetadata()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Metadata.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLegalDocumentDescription}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLegalDocumentDescription}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLegalDocumentDescription}
   * @param {!LegalDocumentDescription} fbeValue LegalDocumentDescription value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLegalDocumentDescription}
   * @param {LegalDocumentDescription} fbeValue LegalDocumentDescription value
   */
  setFields (fbeValue) {
    this.DocumentId.set(fbeValue.DocumentId)
    this.GroupId.set(fbeValue.GroupId)
    this.Metadata.set(fbeValue.Metadata)
  }
}

export { FieldModelLegalDocumentDescription };

/**
 * Fast Binary Encoding LegalDocumentDescription model
 */
class LegalDocumentDescriptionModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLegalDocumentDescription(this.buffer, 4)
  }

  /**
   * Get the LegalDocumentDescription model
   * @this {!LegalDocumentDescriptionModel}
   * @returns {!FieldModelLegalDocumentDescription} model LegalDocumentDescription model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LegalDocumentDescriptionModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LegalDocumentDescriptionModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LegalDocumentDescriptionModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LegalDocumentDescriptionModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLegalDocumentDescription.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LegalDocumentDescriptionModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LegalDocumentDescriptionModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LegalDocumentDescriptionModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LegalDocumentDescriptionModel}
   * @param {!LegalDocumentDescription} value LegalDocumentDescription value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LegalDocumentDescriptionModel}
   * @param {!LegalDocumentDescription} value LegalDocumentDescription value, defaults is new LegalDocumentDescription()
   * @return {!object} Deserialized LegalDocumentDescription value and its size
   */
  deserialize (value = new LegalDocumentDescription()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LegalDocumentDescription(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LegalDocumentDescription(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LegalDocumentDescriptionModel}
   * @param {!number} prev Previous LegalDocumentDescription model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LegalDocumentDescriptionModel };
